// Maior dimensão permitida após o redimensionamento e qualidade do JPEG de
// saída. Sem isso, fotos de celular (sem limite de tamanho no frontend)
// facilmente estouram o limite de 8MB por arquivo configurado no multer do
// backend.
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.8;

// Imagens que já cabem em MAX_DIMENSION e são pequenas não são recomprimidas:
// converter para JPEG só as deixaria maiores/piores (ex: PNG, screenshots).
const SKIP_COMPRESSION_BELOW_BYTES = 300 * 1024;

/**
 * Retorna o próprio `file` (mesma referência) quando não há o que comprimir;
 * caso contrário, um Blob JPEG redimensionado.
 */
async function compressImage(file: File | Blob): Promise<Blob> {
  const dataURI = await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      resolve(reader.result as string);
    });
    reader.readAsDataURL(file);
  });

  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.addEventListener("load", () => resolve());
    img.addEventListener("error", () =>
      reject(new Error("Não foi possível carregar a imagem para compressão."))
    );
    img.src = dataURI;
  });

  const originalWidth = img.naturalWidth;
  const originalHeight = img.naturalHeight;

  const scale = Math.min(
    1,
    MAX_DIMENSION / Math.max(originalWidth, originalHeight)
  );

  if (scale === 1 && file.size <= SKIP_COMPRESSION_BELOW_BYTES) {
    return file;
  }

  const canvasWidth = Math.round(originalWidth * scale);
  const canvasHeight = Math.round(originalHeight * scale);

  const canvas = document.createElement("canvas");
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const context = canvas.getContext("2d")!;
  // JPEG não suporta transparência: preenche de branco antes de desenhar
  // para que imagens com fundo transparente (ex: PNG) não fiquem pretas.
  context.fillStyle = "#FFFFFF";
  context.fillRect(0, 0, canvasWidth, canvasHeight);
  context.drawImage(img, 0, 0, canvasWidth, canvasHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Não foi possível comprimir a imagem."));
        }
      },
      "image/jpeg",
      JPEG_QUALITY
    );
  });
}

export default compressImage;
